<template>
  <div class="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="max-w-7xl mx-auto mb-6">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Deposits vs Sales Report</h1>
            <p class="mt-1 text-sm text-gray-500">Comprehensive summary of all agent activities including deposits, sales, commissions, and reversals</p>
          </div>
          <div class="flex items-center space-x-2">
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              <i class="bx bx-bar-chart-alt-2 mr-1"></i>
              All Agents
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Date Range Filter -->
    <div class="max-w-7xl mx-auto mb-6">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">From Date</label>
            <input 
              type="date" 
              v-model="filters.dateFrom"
              class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">To Date</label>
            <input 
              type="date" 
              v-model="filters.dateTo"
              class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div class="flex items-end">
            <button 
              @click="fetchReport"
              :disabled="loading"
              class="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="bx bx-search mr-1"></i>
              <span v-if="!loading">Generate Report</span>
              <span v-else>Loading...</span>
            </button>
          </div>
          <div class="flex items-end">
            <button 
              @click="exportToCSV"
              :disabled="loading || reportData.length === 0"
              class="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="bx bx-download mr-1"></i> Export CSV
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="max-w-7xl mx-auto mb-6">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-center">
          <i class="bx bx-error-circle text-red-600 text-xl mr-2"></i>
          <p class="text-sm text-red-800">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="max-w-7xl mx-auto">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
        <div class="flex flex-col items-center justify-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p class="text-gray-600">Loading report data...</p>
        </div>
      </div>
    </div>

    <!-- Report Content -->
    <div v-else-if="!loading && reportData.length > 0" class="max-w-7xl mx-auto">
      <!-- Summary Totals -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div class="text-xs font-medium text-gray-500 uppercase mb-1">Total Deposits</div>
              <div class="text-2xl font-bold text-green-600">GMD {{ formatPrice(totals.deposits) }}</div>
              <div class="text-xs text-gray-500 mt-1">{{ formatNumber(totals.depositsCount) }} transactions</div>
            </div>
            <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <i class="bx bx-down-arrow-circle text-2xl text-green-600"></i>
            </div>
          </div>
        </div>

        <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div class="text-xs font-medium text-gray-500 uppercase mb-1">Total Sales</div>
              <div class="text-2xl font-bold text-blue-600">GMD {{ formatPrice(totals.sales) }}</div>
              <div class="text-xs text-gray-500 mt-1">{{ formatNumber(totals.salesCount) }} transactions</div>
            </div>
            <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <i class="bx bx-up-arrow-circle text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div class="text-xs font-medium text-gray-500 uppercase mb-1">Agent Commission</div>
              <div class="text-2xl font-bold text-purple-600">GMD {{ formatPrice(totals.agentCommission) }}</div>
            </div>
            <div class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <i class="bx bx-dollar-circle text-2xl text-purple-600"></i>
            </div>
          </div>
        </div>

        <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div class="text-xs font-medium text-gray-500 uppercase mb-1">Chapman Commission</div>
              <div class="text-2xl font-bold text-orange-600">GMD {{ formatPrice(totals.chapmanCommission) }}</div>
            </div>
            <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <i class="bx bx-receipt text-2xl text-orange-600"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Agents Table -->
      <div class="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-semibold text-gray-700">Agent Performance Summary</h3>
            <span class="text-xs text-gray-500">{{ reportData.length }} agents</span>
          </div>
        </div>
        
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" @click="sortBy('agent_name')">
                  Agent Name
                  <i v-if="sortField === 'agent_name'" :class="sortDirection === 'asc' ? 'bx bx-chevron-up' : 'bx bx-chevron-down'" class="text-xs"></i>
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" @click="sortBy('total_deposits')">
                  Deposits
                  <i v-if="sortField === 'total_deposits'" :class="sortDirection === 'asc' ? 'bx bx-chevron-up' : 'bx bx-chevron-down'" class="text-xs"></i>
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" @click="sortBy('total_sales')">
                  Sales
                  <i v-if="sortField === 'total_sales'" :class="sortDirection === 'asc' ? 'bx bx-chevron-up' : 'bx bx-chevron-down'" class="text-xs"></i>
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" @click="sortBy('total_deposit_reversals')">
                  Reversals
                  <i v-if="sortField === 'total_deposit_reversals'" :class="sortDirection === 'asc' ? 'bx bx-chevron-up' : 'bx bx-chevron-down'" class="text-xs"></i>
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" @click="sortBy('net_activity')">
                  Net Activity
                  <i v-if="sortField === 'net_activity'" :class="sortDirection === 'asc' ? 'bx bx-chevron-up' : 'bx bx-chevron-down'" class="text-xs"></i>
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent Commission
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Chapman Commission
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="agent in sortedReportData" :key="agent.agent_id" class="hover:bg-gray-50 transition-colors">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ agent.agent_name }}</div>
                  <div class="text-xs text-gray-500">ID: {{ agent.agent_id }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <div class="text-sm font-medium text-green-600">GMD {{ formatPrice(agent.total_deposits) }}</div>
                  <div class="text-xs text-gray-500">{{ formatNumber(agent.deposits_count) }} txns</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <div class="text-sm font-medium text-blue-600">GMD {{ formatPrice(agent.total_sales) }}</div>
                  <div class="text-xs text-gray-500">{{ formatNumber(agent.sales_count) }} txns</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <div class="text-sm font-medium text-red-600">GMD {{ formatPrice(agent.total_deposit_reversals) }}</div>
                  <div class="text-xs text-gray-500">{{ formatNumber(agent.deposit_reversals_count) }} txns</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <div class="text-sm font-bold" :class="calculateNetActivity(agent) >= 0 ? 'text-green-600' : 'text-red-600'">
                    {{ calculateNetActivity(agent) >= 0 ? '+' : '' }}GMD {{ formatPrice(calculateNetActivity(agent)) }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <div class="text-sm font-medium text-purple-600">GMD {{ formatPrice(agent.total_commission) }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <div class="text-sm font-medium text-orange-600">GMD {{ formatPrice(agent.chapman_commission) }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && reportData.length === 0 && !error" class="max-w-7xl mx-auto">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
        <div class="text-center">
          <i class="bx bx-file text-6xl text-gray-400 mb-4"></i>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
          <p class="text-sm text-gray-500 mb-4">No agent data found for the selected date range.</p>
          <p class="text-xs text-gray-400">Try adjusting your date filters and generate the report again.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DepositsVsSalesReport',
  
  data() {
    return {
      reportData: [],
      loading: false,
      error: null,
      filters: {
        dateFrom: '',
        dateTo: ''
      },
      sortField: 'agent_name',
      sortDirection: 'asc'
    };
  },

  computed: {
    totals() {
      return {
        deposits: this.reportData.reduce((sum, agent) => sum + parseFloat(agent.total_deposits || 0), 0),
        depositsCount: this.reportData.reduce((sum, agent) => sum + parseInt(agent.deposits_count || 0), 0),
        sales: this.reportData.reduce((sum, agent) => sum + parseFloat(agent.total_sales || 0), 0),
        salesCount: this.reportData.reduce((sum, agent) => sum + parseInt(agent.sales_count || 0), 0),
        agentCommission: this.reportData.reduce((sum, agent) => sum + parseFloat(agent.total_commission || 0), 0),
        chapmanCommission: this.reportData.reduce((sum, agent) => sum + parseFloat(agent.chapman_commission || 0), 0),
        reversals: this.reportData.reduce((sum, agent) => sum + parseFloat(agent.total_deposit_reversals || 0), 0),
        reversalsCount: this.reportData.reduce((sum, agent) => sum + parseInt(agent.deposit_reversals_count || 0), 0)
      };
    },

    sortedReportData() {
      const data = [...this.reportData];
      
      return data.sort((a, b) => {
        let aVal, bVal;

        if (this.sortField === 'net_activity') {
          aVal = this.calculateNetActivity(a);
          bVal = this.calculateNetActivity(b);
        } else if (this.sortField === 'agent_name') {
          aVal = (a[this.sortField] || '').toLowerCase();
          bVal = (b[this.sortField] || '').toLowerCase();
        } else {
          aVal = parseFloat(a[this.sortField] || 0);
          bVal = parseFloat(b[this.sortField] || 0);
        }

        if (this.sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }
  },

  created() {
    this.setDefaultDates();
    this.fetchReport();
  },

  methods: {
    setDefaultDates() {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      
      this.filters.dateFrom = firstDay.toISOString().split('T')[0];
      this.filters.dateTo = today.toISOString().split('T')[0];
    },

    async fetchReport() {
      if (!this.filters.dateFrom || !this.filters.dateTo) {
        this.error = 'Please select both start and end dates';
        return;
      }

      const fromDate = new Date(this.filters.dateFrom);
      const toDate = new Date(this.filters.dateTo);

      if (fromDate > toDate) {
        this.error = 'Start date must be before end date';
        return;
      }

      this.loading = true;
      this.error = null;
      
      try {
        const response = await axios.get('/report/generate', {
          params: {
            report: 'deposits_vs_sales',
            from: this.filters.dateFrom,
            to: this.filters.dateTo
          }
        });
        
        this.reportData = response.data || [];
        
        if (this.reportData.length === 0) {
          console.log('No data returned from API');
        }
      } catch (err) {
        console.error('Error fetching report:', err);
        
        if (err.response) {
          switch (err.response.status) {
            case 401:
              this.error = 'Authentication required. Please log in.';
              break;
            case 422:
              this.error = 'Invalid parameters. Check date format.';
              break;
            case 500:
              this.error = 'Server error. Please try again later.';
              break;
            default:
              this.error = 'An unexpected error occurred.';
          }
        } else if (err.request) {
          this.error = 'Network error. Please check your connection.';
        } else {
          this.error = err.message;
        }
      } finally {
        this.loading = false;
      }
    },

    calculateNetActivity(agent) {
      return parseFloat(agent.total_deposits || 0) - 
             parseFloat(agent.total_sales || 0) - 
             parseFloat(agent.total_deposit_reversals || 0);
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'desc';
      }
    },

    formatPrice(value) {
      if (!value && value !== 0) return '0.00';
      const val = parseFloat(value).toFixed(2);
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    exportToCSV() {
      const headers = [
        'Agent ID',
        'Agent Name',
        'Total Deposits',
        'Deposits Count',
        'Total Sales',
        'Sales Count',
        'Agent Commission',
        'Chapman Commission',
        'Deposit Reversals',
        'Reversals Count',
        'Net Activity'
      ];

      const rows = this.reportData.map(agent => {
        const netActivity = this.calculateNetActivity(agent);
        return [
          agent.agent_id,
          `"${agent.agent_name}"`,
          agent.total_deposits,
          agent.deposits_count,
          agent.total_sales,
          agent.sales_count,
          agent.total_commission,
          agent.chapman_commission,
          agent.total_deposit_reversals,
          agent.deposit_reversals_count,
          netActivity
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deposits_vs_sales_${this.filters.dateFrom}_to_${this.filters.dateTo}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }
};
</script>
